import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, RotateCcw, AlertCircle, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Returns = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-xl font-bold">Returns & Exchanges</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Return Policy Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Return Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold">30-Day Return Window</h3>
                  <p className="text-sm text-muted-foreground">
                    Return items within 30 days of delivery for a full refund
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold">Free Returns</h3>
                  <p className="text-sm text-muted-foreground">
                    Free return shipping on all eligible items
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold">Easy Process</h3>
                  <p className="text-sm text-muted-foreground">
                    Simple online return process with tracking
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold">Quality Guarantee</h3>
                  <p className="text-sm text-muted-foreground">
                    We guarantee the quality of all our products
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Return Conditions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Return Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Eligible for Return</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Items in original condition</li>
                  <li>• All tags and packaging included</li>
                  <li>• No signs of wear or damage</li>
                  <li>• Returned within 30 days</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Not Eligible for Return</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Custom or personalized items</li>
                  <li>• Items damaged by misuse</li>
                  <li>• Items returned after 30 days</li>
                  <li>• Items without original packaging</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Return Process */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>How to Return an Item</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold">Contact Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Email us at info@orctatech.com with your order number and
                    reason for return
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold">Get Return Label</h3>
                  <p className="text-sm text-muted-foreground">
                    We'll send you a prepaid return label and instructions
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold">Package & Ship</h3>
                  <p className="text-sm text-muted-foreground">
                    Package your item securely and drop it off at any shipping
                    location
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold">Receive Refund</h3>
                  <p className="text-sm text-muted-foreground">
                    Once we receive and inspect your return, we'll process your
                    refund
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800">
                  Important Notes
                </h3>
                <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                  <li>
                    • Refunds are processed within 5-7 business days after we
                    receive your return
                  </li>
                  <li>• Original shipping costs are non-refundable</li>
                  <li>
                    • Custom designs cannot be returned unless there's a
                    manufacturing defect
                  </li>
                  <li>
                    • For exchanges, please contact us before returning your
                    item
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Returns;
